import React, { FC, useCallback, useContext, useState } from "react";
import EditLinkDialog from "./EditLinkDialog";
import { TiptapEditorContext } from "./contexts/TiptapEditorContext";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@src/lib/ui/Tooltip";
import TextBoldIcon from "@src/lib/icons/18px/text-bold";
import { Button } from "@src/lib/ui/Button";
import TextItalicIcon from "@src/lib/icons/18px/text-italic";
import OrderedListIcon from "@src/lib/icons/18px/ordered-list";
import UnorderedListIcon from "@src/lib/icons/18px/unordered-list";
import LinkIcon from "@src/lib/icons/18px/link";
import PhotoOutlineIcon from "@src/lib/icons/18px/photo-outline";
import { Separator } from "@src/lib/ui/Separator";
import MessageBlastTextStyleAction from "@src/features/libraryItem/MessageBlastTextStyleAction";
import AddMediaDialog from "@src/features/app/dialogs/addMedia/AddMediaDialog";
import { MediaFragment } from "@src/fragments.generated";
import { LinkType } from "@src/lib/utils/files";
import EmojiPicker from "../../../lib/ui/EmojiPicker";
import { Popover, PopoverContent, PopoverTrigger } from "@src/lib/ui/Popover";
import EmojiOutlineIcon from "@src/lib/icons/18px/emoji-outline";
import { NodeSelection } from "@tiptap/pm/state";

type Props = {
  hideRichTextHeaderOptions?: boolean;
  hideImageUpload?: boolean;
  hideSeparators?: boolean;
  hideListOptions?: boolean;
  showEmojiPicker?: boolean;
  buttonStyle?: "outline" | "ghost";
};

const RichTextEditorFormattingToolbar: FC<Props> = ({
  hideRichTextHeaderOptions = false,
  hideImageUpload = false,
  hideSeparators = false,
  hideListOptions = false,
  showEmojiPicker = false,
  buttonStyle = "ghost",
}) => {
  const [addLinkOpen, setAddLinkOpen] = useState(false);
  const [addLinkText, setAddLinkText] = useState("");
  const [addLinkSelection, setAddLinkSelection] = useState<{
    from: number;
    to: number;
  } | null>(null);
  const tiptapEditorContext = useContext(TiptapEditorContext);

  const handleImageUpload = (media: MediaFragment) => {
    if (!tiptapEditorContext?.editor) return;
    const imageUrl =
      media.unoptimizedUrl || media.imageUrls?.original || media.mediaUrls.en;
    if (!imageUrl) return;
    const editor = tiptapEditorContext.editor;
    const { selection } = editor.state;

    if (
      selection instanceof NodeSelection &&
      selection.node.type.name === editor.schema.nodes.image?.name
    ) {
      editor
        .chain()
        .focus()
        .setTextSelection(selection.to)
        .setImage({ src: imageUrl })
        .run();
      return;
    }

    editor.chain().focus().setImage({ src: imageUrl }).run();
  };

  const onEmojiSelect = useCallback(
    (emoji: string) => {
      if (tiptapEditorContext?.editor) {
        const insertPoint =
          tiptapEditorContext.editor.view?.state?.selection?.anchor || 1;
        tiptapEditorContext.editor
          .chain()
          .focus()
          .insertContentAt(insertPoint, emoji, {
            parseOptions: {
              preserveWhitespace: false,
            },
          })
          .run();
      }
    },
    [tiptapEditorContext?.editor],
  );

  return (
    <TooltipProvider>
      <div className="flex flex-1 justify-between gap-1 self-stretch">
        {!hideRichTextHeaderOptions && <MessageBlastTextStyleAction />}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant={
                tiptapEditorContext?.formatting.isBold
                  ? "secondary"
                  : buttonStyle
              }
              size="icon"
              onClick={() => {
                if (!tiptapEditorContext?.editor) return;
                tiptapEditorContext.editor.chain().focus().toggleBold().run();
              }}
            >
              <TextBoldIcon />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom">Bold</TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant={
                tiptapEditorContext?.formatting.isItalic
                  ? "secondary"
                  : buttonStyle
              }
              size="icon"
              onClick={() => {
                tiptapEditorContext?.editor
                  ?.chain()
                  .focus()
                  .toggleItalic()
                  .run();
              }}
            >
              <TextItalicIcon />
            </Button>
          </TooltipTrigger>
          {!hideSeparators && (
            <div className="mx-1 py-1">
              <Separator orientation="vertical" />
            </div>
          )}
          <TooltipContent side="bottom">Italic</TooltipContent>
        </Tooltip>
        {!hideListOptions && (
          <>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant={
                    tiptapEditorContext?.formatting.isBulletList
                      ? "secondary"
                      : buttonStyle
                  }
                  size="icon"
                  onClick={() => {
                    tiptapEditorContext?.editor
                      ?.chain()
                      .focus()
                      .toggleBulletList()
                      .run();
                  }}
                >
                  <UnorderedListIcon />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">Bulleted List</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant={
                    tiptapEditorContext?.formatting.isOrderedList
                      ? "secondary"
                      : buttonStyle
                  }
                  size="icon"
                  onClick={() => {
                    tiptapEditorContext?.editor
                      ?.chain()
                      .focus()
                      .toggleOrderedList()
                      .run();
                  }}
                >
                  <OrderedListIcon />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">Numbered List</TooltipContent>
            </Tooltip>
          </>
        )}
        {!hideSeparators && (
          <div className="mx-1 py-1">
            <Separator orientation="vertical" />
          </div>
        )}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant={buttonStyle}
              size="icon"
              onClick={() => {
                const selection =
                  tiptapEditorContext?.editor?.view.state.selection;
                if (!selection) return;
                const text =
                  tiptapEditorContext?.editor?.view.state.doc.textBetween(
                    selection.from,
                    selection.to,
                    " ",
                  );
                setAddLinkText(text ?? "");
                setAddLinkSelection({
                  from: selection.from,
                  to: selection.to,
                });
                setAddLinkOpen(true);
              }}
            >
              <LinkIcon />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom">Link</TooltipContent>
        </Tooltip>
        {!hideImageUpload && (
          <Tooltip>
            <TooltipTrigger asChild>
              <AddMediaDialog
                onMediaChange={(media) => {
                  if (media) {
                    handleImageUpload(media);
                  }
                }}
                onResourceChange={(trainingResource) => {
                  const newMedia = trainingResource.publishedVersion?.media;
                  if (!newMedia) return;
                  handleImageUpload(newMedia);
                }}
                filterResources={[LinkType.IMAGE, LinkType.GIF]}
                uploadAccept=".gif,.png,.jpg,.jpeg,.webp"
                title="Add image"
                trigger={
                  <Button size="icon" variant={buttonStyle}>
                    <PhotoOutlineIcon />
                  </Button>
                }
              />
            </TooltipTrigger>
            <TooltipContent side="bottom">Add image</TooltipContent>
          </Tooltip>
        )}
        {showEmojiPicker && (
          <Popover>
            <Tooltip>
              <TooltipTrigger asChild>
                <PopoverTrigger asChild>
                  <Button size="icon" variant={buttonStyle}>
                    <EmojiOutlineIcon />
                  </Button>
                </PopoverTrigger>
              </TooltipTrigger>
              <TooltipContent side="bottom">Emoji</TooltipContent>
            </Tooltip>
            <PopoverContent
              side="right"
              align="start"
              className="z-[9999] w-auto p-0"
              sideOffset={5}
            >
              <EmojiPicker onEmojiSelect={onEmojiSelect} />
            </PopoverContent>
          </Popover>
        )}
      </div>
      {addLinkOpen && (
        <EditLinkDialog
          open={addLinkOpen}
          onOpenChange={setAddLinkOpen}
          text={addLinkText}
          onComplete={(text, link) => {
            if (!addLinkSelection) return;
            tiptapEditorContext?.editor
              ?.chain()
              .focus()
              .setLink({ href: link, target: "_blank" })
              .insertContentAt(
                {
                  from: addLinkSelection.from,
                  to: addLinkSelection.to,
                },
                text,
              )
              .unsetMark("link")
              .run();
          }}
        />
      )}
    </TooltipProvider>
  );
};

export default RichTextEditorFormattingToolbar;
